<template>
  <div class="d-flex flex-column mt-1 mb-4 justify-content-center">
    <div
      class="input-wrapper"
      :class="{
        fill,
        error,
        disabled,
        touched: highlight,
      }"
    >
      <div v-if="label" class="input-label">
        {{ label }}
      </div>
      <input
        :class="getInputClasses()"
        :maxlength="maxLength"
        :style="inputStyle"
        :type="type"
        :placeholder="placeholder"
        :value="value"
        @input="($event) => handleInput($event.target.value)"
        @blur="$emit('onBlur', $event)"
        @focus="$emit('onFocus', $event)"
        :id="id"
        :name="name"
        :minlength="minLength"
      />
      <div v-if="showErrorIcon" class="error-icon-wrapper">
        <i class="fas fa-exclamation" />
      </div>
    </div>
    <div v-if="shouldShowErrorMessage" class="error-msg">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";

export default defineComponent({
  name: "TextInput",
  props: {
    value: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "text",
    },
    maxLength: {
      type: Number,
      default: null,
    },
    fill: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    errorIcon: {
      type: Boolean,
      default: true,
    },
    inputStyle: Object,
    centeredText: {
      type: Boolean,
      default: false,
    },
    highlight: {
      type: Boolean,
      default: false,
    },
    showErrorMessage: {
      type: Boolean,
      default: true,
    },
    id: {
      type: String,
      default: "",
    },
    name: {
      type: String,
      default: "",
    },
    minLength: {
      type: Number,
      default: null,
    },
  },
  emits: ["update:value", "onBlur", "onFocus"],
  setup(props) {
    const showErrorIcon = computed(() => {
      if (props.error && props.errorIcon) {
        return true;
      }
      return false;
    });

    const shouldShowErrorMessage = computed(() => {
      if (props.error && props.showErrorMessage) {
        return true;
      }
      return false;
    });

    const touched = ref<boolean>(false);

    return {
      showErrorIcon,
      shouldShowErrorMessage,
      touched,
    };
  },
  methods: {
    handleInput(newValue: string) {
      this.$emit("update:value", newValue);
    },
    getInputClasses() {
      const classes = {};
      classes["input-element"] = true;
      classes["text-center"] = this.centeredText && true;
      classes["with-label"] = this.value && this.label;
      classes["shrink"] = this.errorMessage !== "" || this.error;
      return classes;
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
$pj-color-red-opaque: rgba(222, 14, 14, 0.4);
.input-wrapper {
  height: 47px;
  background-color: white;
  border: 1px solid #d1d1d1;
  border-radius: 5px;
  padding: 5px 12px;
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  justify-content: center;

  &:focus-within {
    border: 1px solid $pj-color-black;
  }

  &.fill {
    width: 100%;
  }

  &.error {
    border: 1px solid $pj-color-red !important;
  }

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
  &.touched {
    border: 1px solid #aed3ad;
  }
}

.input-label {
  position: absolute;
  top: -10px;
  left: 10px;
  font-size: 14px;
  font-weight: 400;
  background-color: white;
  padding: 0 5px;
  color: #595a5a;
  pointer-events: none;
  font-family: Inter;
}

.highlighted {
  border: 1px solid #aed3ad;
}

.input-element {
  color: #111111;
  font-weight: 500;
  font-size: 14px;
  border: none;
  flex-grow: 1;
  height: 100%;
  width: 100%;
  font-family: Inter;

  &:focus-visible,
  &:focus,
  &:active {
    outline-width: 0;
  }

  &::placeholder {
    color: #8b8b8b;
    font-weight: 400;
    font-size: 14px;
    border: none;
    flex-grow: 1;
    height: 100%;
    width: 100%;
    padding-top: 10px;
  }
  &.with-label {
    padding-top: 10px;
  }

  &.shrink {
    flex-shrink: 20;
  }
}
.error-icon-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  color: $pj-color-white;
  width: 36px;
  height: 36px;
  border-radius: 18px;
  background-color: $pj-color-red;
  border: 8px solid $pj-color-red-opaque;
  -webkit-background-clip: padding-box; /* for Safari */
  background-clip: padding-box; /* for IE9+, Firefox 4+, Opera, Chrome */
  font-size: 12px;

  i {
    margin: 0;
  }
}

.error-msg {
  color: #d80000;
  font-size: 12px;
  font-family: Inter;
  font-weight: 400;
}
</style>
